import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { Link } from '@zola/zola-ui/src/components/Link';

import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';

import styles from './ProductColorPicker.module.less';

interface ColorObj {
  value?: string;
  label?: string;
}

export interface ProductColorPickerProps {
  colors?: ColorObj[];
  activeColor?: string;
  maxColorsToShow?: number;
  link?: string;
  onSelect: (c: ColorObj) => void;
  swatchWidth?: number;
  disableMobileSwatchSizing?: boolean;
  onMoreClick?: () => void;
  className?: string;
}

function ProductColorPicker({
  colors,
  activeColor,
  maxColorsToShow,
  link,
  onSelect,
  swatchWidth,
  disableMobileSwatchSizing,
  onMoreClick,
  className,
}: ProductColorPickerProps): React.ReactElement | null {
  const [isMobileView, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobile());
  }, []);

  if (!colors) return null;

  const visibleColors =
    maxColorsToShow && colors.length > maxColorsToShow ? colors.slice(0, maxColorsToShow) : colors;
  const showMoreLink = maxColorsToShow && colors.length > maxColorsToShow;

  let frontCircleR: string;
  let backCircleR: string;
  let activeCircleR: string;
  let transformFront: string;
  let transformBack: string;

  if (swatchWidth && (!isMobileView || disableMobileSwatchSizing)) {
    const backWidth = swatchWidth + 1;
    frontCircleR = `${swatchWidth}px`;
    backCircleR = `${backWidth}px`;
    activeCircleR = `${swatchWidth + 8}px`;
    transformFront = `translate(-${swatchWidth / 2}px, -${swatchWidth / 2}px)`;
    transformBack = `translate(-${backWidth / 2}px, -${backWidth / 2}px)`;
  }

  return (
    <div className={cx(styles.productColorPicker, className)}>
      {visibleColors.map(color => {
        let wrapperStyle;
        let circleBackStyle: React.CSSProperties = {};
        let circleFrontStyle: React.CSSProperties = { background: `#${color.value}` };
        if (swatchWidth && (!isMobileView || disableMobileSwatchSizing)) {
          wrapperStyle = {
            width: activeCircleR,
            height: activeCircleR,
            border: activeColor === color.value ? '1px solid #21201F' : '1px solid transparent',
          };
          circleBackStyle = {
            width: backCircleR,
            height: backCircleR,
            transform: transformBack,
            border: '1px solid #C0C0C0',
          };
          circleFrontStyle = {
            background: `#${color.value}`,
            width: frontCircleR,
            height: frontCircleR,
            transform: transformFront,
          };
        }
        return (
          <span
            key={color.value}
            className={cx(styles.circleWrapper, { [styles.active]: activeColor === color.value })}
            onClick={(): void => onSelect(color)}
            style={wrapperStyle}
          >
            <span className={cx(styles.circle, styles.back)} style={circleBackStyle} />
            <span
              className={cx(styles.circle, styles.front)}
              data-label={color.label}
              style={circleFrontStyle}
            />
          </span>
        );
      })}
      {link && showMoreLink && (
        <Link className="zola-ui link more" href={link} aria-label="view more color options">
          more
        </Link>
      )}
      {onMoreClick && showMoreLink && (
        <Link role="button" onClick={onMoreClick} aria-label="view more color options">
          More
        </Link>
      )}
    </div>
  );
}

export default ProductColorPicker;
